import type { CSSProperties } from "react";
import { cx } from "./canvasTheme";
import { PrintColorPreviewGrid } from "./PrintColorPreviewGrid";
import {
  printColorModeOptions,
  printPaperOptions,
  type PrintColorMode,
  type PrintColorPreview,
  type PrintPaperFormat,
  type PrintTitleBlockMode,
} from "./printExportTypes";

export type { PrintColorMode, PrintColorPreview, PrintPaperFormat, PrintTitleBlockMode } from "./printExportTypes";

type PrintExportPanelProps = {
  isOpen: boolean;
  panelStyle?: CSSProperties;
  onActivate?: () => void;
  paperFormat: PrintPaperFormat;
  onPaperFormatChange: (value: PrintPaperFormat) => void;
  titleBlockMode: PrintTitleBlockMode;
  onTitleBlockModeChange: (value: PrintTitleBlockMode) => void;
  colorMode: PrintColorMode;
  onColorModeChange: (value: PrintColorMode) => void;
  savedTitleBlockEntityCount: number;
  selectedEntityCount: number;
  printableEntityCount: number;
  computedScaleLabel: string;
  colorPreview: PrintColorPreview[];
  status: string | null;
  onSaveTitleBlockSelection: () => void;
  onExportPdf: () => void;
};

const iconButtonClass =
  'inline-flex h-10 w-10 items-center justify-center rounded-md border border-[rgba(74,58,31,0.12)] bg-white text-[#1f2937] transition active:scale-110';
const tabButtonClass = "rounded-md px-2 py-1 text-[10px] font-semibold transition";
const activeTabClass = "bg-white text-[#1f2937] shadow-sm";
const inactiveTabClass = "text-[#6c5b3d] hover:bg-white/60";

export function PrintExportPanel({
  isOpen,
  panelStyle,
  onActivate,
  paperFormat,
  onPaperFormatChange,
  titleBlockMode,
  onTitleBlockModeChange,
  colorMode,
  onColorModeChange,
  savedTitleBlockEntityCount,
  selectedEntityCount,
  printableEntityCount,
  computedScaleLabel,
  colorPreview,
  status,
  onSaveTitleBlockSelection,
  onExportPdf,
}: PrintExportPanelProps) {
  if (!isOpen) return null;

  return (
    <aside
      className="absolute bottom-20 left-[92px] z-20 flex w-[390px] flex-col gap-3 rounded-[6px] border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.98)] p-3 shadow-[0_20px_40px_rgba(58,45,20,0.14)] backdrop-blur-[10px]"
      style={panelStyle}
      onMouseDownCapture={onActivate}
      onFocusCapture={onActivate}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="m-0 text-[11px] font-bold uppercase tracking-[0.08em] text-[#4b3e27]">Stampa / Export</h3>
          <div className="mt-1 text-[10px] text-[#6c5b3d]">{printableEntityCount} entita nel layout</div>
        </div>
        <div className="rounded-full bg-[rgba(29,92,171,0.12)] px-2 py-1 text-[10px] font-semibold text-[#1d5cab]">
          {computedScaleLabel}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-1 rounded-md bg-[rgba(74,58,31,0.08)] p-1">
        {printPaperOptions.map((option) => (
          <button
            key={option}
            type="button"
            className={cx(tabButtonClass, paperFormat === option ? activeTabClass : inactiveTabClass)}
            onClick={() => onPaperFormatChange(option)}
          >
            {option}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          className={`rounded-md border px-3 py-2 text-left text-[11px] font-semibold transition active:scale-[0.99] ${
            titleBlockMode === 'standard'
              ? 'border-[rgba(29,92,171,0.24)] bg-[rgba(29,92,171,0.08)] text-[#1d5cab]'
              : 'border-[rgba(74,58,31,0.12)] bg-white text-[#1f2937]'
          }`}
          onClick={() => onTitleBlockModeChange('standard')}
        >
          Cartiglio standard
        </button>
        <button
          type="button"
          className={`rounded-md border px-3 py-2 text-left text-[11px] font-semibold transition active:scale-[0.99] ${
            titleBlockMode === 'saved'
              ? 'border-[rgba(29,92,171,0.24)] bg-[rgba(29,92,171,0.08)] text-[#1d5cab]'
              : 'border-[rgba(74,58,31,0.12)] bg-white text-[#1f2937]'
          }`}
          onClick={() => onTitleBlockModeChange('saved')}
        >
          Cartiglio salvato
          <span className="mt-1 block text-[10px] font-medium text-[#6c5b3d]">
            {savedTitleBlockEntityCount} entita salvate
          </span>
        </button>
      </div>

      <div className="grid grid-cols-[1fr_auto] gap-2">
        <button
          type="button"
          className="rounded-md border border-[rgba(74,58,31,0.12)] bg-white px-3 py-2 text-left text-[11px] font-semibold text-[#1f2937] transition active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-45"
          onClick={onSaveTitleBlockSelection}
          disabled={selectedEntityCount === 0}
        >
          Salva selezione cartiglio
          <span className="mt-1 block text-[10px] font-medium text-[#6c5b3d]">
            {selectedEntityCount > 0 ? `${selectedEntityCount} entita selezionate` : 'Seleziona entita da salvare'}
          </span>
        </button>
        <button type="button" className={iconButtonClass} aria-label="Export PDF" title="Export PDF" onClick={onExportPdf}>
          <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
            <path d="M6 3h8l4 4v14H6V3Z" strokeWidth="1.8" strokeLinejoin="round" />
            <path d="M14 3v5h4M8 16h8M8 12h8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <div className="flex flex-col gap-2 rounded-md border border-[rgba(74,58,31,0.12)] bg-white/70 p-2">
        <div className="grid grid-cols-3 gap-1 rounded-md bg-[rgba(74,58,31,0.08)] p-1">
          {printColorModeOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={cx(tabButtonClass, colorMode === option.value ? activeTabClass : inactiveTabClass)}
              onClick={() => onColorModeChange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <PrintColorPreviewGrid colorPreview={colorPreview} />
      </div>

      {status && <div className="rounded-md bg-white/70 px-3 py-2 text-[11px] text-[#4b3e27]">{status}</div>}
    </aside>
  );
}
